import * as Yup from 'yup'

export enum ArbitrationState {
  None = 0,
  New = 1,
  Submitted = 2,
  Expired = 3
}

export enum ArbitrationIssueType {
  PhysicalDamage = 1,
  NonPhysicalDamage = 2
}

export interface IArbitrationLookup {
  id: number
  description: string
}

export interface IArbitrationExistingDocument {
  id: number
  fileName: string
}

export interface IArbitrationMetadata {
  fileSizeLimit: number // MB
  attachmentFormats: string[]
  maxMileageExceed: number
  maxDocumentCount: number
  physicalDamageMinDocuments: number
  nonPhysicalDamageMinDocuments: number
  attachmentsDisabled: boolean
  issueTypes: IArbitrationLookup[]
  resolutionTypes: IArbitrationLookup[]
  contactMethods: IArbitrationLookup[]
}

export interface IArbitrationDetail {
  vehicleArbitrationID: number | null
  salesforceCaseID: string | null
  arbitrationState: ArbitrationState
  latestArbitrationStatus: string | null
  contactName: string
  contactEmail: string
  contactPhone: string
  contactMethodID: number
  issueTypeID: number
  resolutionTypeID: number
  currentMileage: number
  reason: string
  vin: string
  modelYear: string
  model: string
  grade: string
  saleMileage: number
  salePrice: number
  saleDate: string
  announcements: string[]
  existingDocuments: IArbitrationExistingDocument[]
}

export interface IArbitrationDocumentSubmit {
  fileName: string
  fileContent: string // base64
  mimeType: string
}

export interface IArbitrationSubmitRequest {
  vehicleInstanceID: number
  buyerVehiclePurchaseID: number
  vin: string
  contactName: string
  contactEmail: string
  contactPhone: string
  contactMethodID: number
  issueTypeID: number
  resolutionTypeID: number
  currentMileage: number
  reason: string
  documents: IArbitrationDocumentSubmit[]
}

export interface IArbitrationAttachmentRequest {
  vehicleInstanceID: number
  arbitrationID: number
  salesforceCaseID: string
  vin: string
  documents: IArbitrationDocumentSubmit[]
}

export interface IArbitrationResult {
  success: boolean
  arbitrationState: ArbitrationState
  salesforceCaseID: string
  message: string
}

export interface IArbitrationFormValues {
  contactName: string
  contactEmail: string
  contactPhone: string
  contactMethodID: number
  issueTypeID: number
  resolutionTypeID: number
  currentMileage: string
  reason: string
  documents: IArbitrationDocumentSubmit[]
}

export const ARBITRATION_MAX_DOCUMENTS = 10
export const ARBITRATION_MIN_PHYSICAL_DAMAGE_DOCUMENTS = 4
export const ARBITRATION_REASON_MAX_LENGTH = 4000

export const initialArbitrationValues: IArbitrationFormValues = {
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  contactMethodID: 0,
  issueTypeID: 0,
  resolutionTypeID: 2,
  currentMileage: '',
  reason: '',
  documents: []
}

export const arbitrationFormSchema = Yup.object({
  contactName: Yup.string().trim().required('Name is required'),
  contactEmail: Yup.string().email('Email address format is not correct').required('Email is required'),
  contactPhone: Yup.string()
    .required('Phone number is required')
    .matches(/^[0-9]{3}-[0-9]{3}-[0-9]{4}$/, 'Phone number format must be 123-456-7890'),
  contactMethodID: Yup.number().min(1, 'Select a contact method').required('Contact method is required'),
  issueTypeID: Yup.number().min(1, 'Issue type is required').required('Issue type is required'),
  resolutionTypeID: Yup.number()
    .min(1, 'Resolution requested is required')
    .required('Resolution requested is required'),
  currentMileage: Yup.string()
    .required('Current mileage is required')
    .matches(/^[0-9]+$/, 'Current mileage must be number'),
  reason: Yup.string()
    .trim()
    .max(ARBITRATION_REASON_MAX_LENGTH, 'Reason must be 4000 characters or fewer')
    .required('Reason is required'),
  documents: Yup.array()
})
